/**
 * mapScannedReceiptToExpenseDraft — Adapter ScannedReceipt → ExpenseDraft (M13a-4).
 *
 * Wandelt einen `status==='extracted'` Beleg-Scan (KI-OCR, UNTRUSTED) in einen
 * `ExpenseDraft` zur Modal-Vorbefüllung. Implementiert exakt die BINDENDE
 * T-2-Tabelle (`t2-adapter-soll-m13a-4.md`) des [E-RECHNUNG]-Experten.
 *
 * Zentrale Abweichungen vom ZUGFeRD-Pfad (`mapReceivedInvoiceToExpenseDraft`):
 *   E-2  — Der Draft setzt `vatDeductible=false`, `vatDeductibleCents=0` und jede
 *          `vatBreakdown[].vatDeductibleCents=0` IMMER. Der §15-Vorsteuerabzug wird
 *          NIE aus un-bestätigtem OCR abgeleitet. Erst das `ReceiptScanModal`
 *          berechnet ihn nach feldweiser Mensch-Bestätigung.
 *   E-3  — `documentType==='credit_note'` → `documentTypeCode='381'` + alle Cent-
 *          Beträge per `-Math.abs(...)` negativ (§17 Abs.1 UStG Vorsteuer-Korrektur).
 *
 * Wiederverwendung (KEIN Copy-Paste, Golden Rule): `deriveTaxScheme` und
 * `checkInputInvoiceCompleteness` aus `expenseTax.ts`. Da `ScannedReceipt` nicht
 * die `ParsedZugferdData`-Form trägt, synthetisiert der Adapter fail-closed einen
 * `TaxCompletenessInput` (`synthInput`) als reine Wegwerf-Struktur (§1 der T-2).
 *
 * Beträge stammen AUSSCHLIESSLICH aus den server-validierten Cents
 * (`receipt.normalizedBreakdown` / `receipt.total*Cents`), niemals aus den
 * UNTRUSTED EUR-Floats in `receipt.extraction`.
 *
 * Pure function — keine Seiteneffekte, keine Netzwerk-/Firebase-Aufrufe.
 *
 * @module mapScannedReceiptToExpenseDraft
 */

import { centsToEuros } from './moneyUtils';
import {
  deriveTaxScheme,
  checkInputInvoiceCompleteness,
  TaxCompletenessInput,
  ExpenseSellerContext,
} from './expenseTax';
import {
  ScannedReceipt,
  ReceiptValidationFlag,
} from '../types/scannedReceiptTypes';
import {
  TaxScheme,
  ExpenseDraft,
  ExpenseVatBreakdownEntry,
} from '../types/expenseTypes';
import {
  ReceivedInvoiceLineItem,
  ReceivedInvoiceTaxBreakdown,
} from '../types/receivedInvoiceTypes';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * True when a string is non-empty after trimming. Mirrors the private helper in
 * `expenseTax.ts` (T-2 §1).
 */
function nonEmpty(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Converts integer cents to a EUR decimal, treating `null` as `0`.
 *
 * `centsToEuros` throws on a non-integer/non-finite input; the server-normalized
 * cents may be `null` when an amount was not derivable. Fail-closed to `0` so the
 * §33-threshold / Entgelt-presence checks see a deterministic value.
 */
function centsToEurosSafe(cents: number | null): number {
  return cents == null ? 0 : centsToEuros(cents);
}

/**
 * Flags whose presence makes the steuerliche Behandlung / der Buchungs-Betrag
 * unzuverlässig → `finalScheme='needs_review'` (T-2 §4). FELD-QUALITÄTS-Flags
 * (`low_confidence`, `missing_required_field`, `date_unparseable`) sind bewusst
 * NICHT enthalten — sie wirken über `completenessStatus` und die Modal-Buchen-
 * Sperre, nicht über die Scheme-Klassifikation.
 */
const TREATMENT_UNCERTAINTY: ReadonlySet<ReceiptValidationFlag> = new Set<ReceiptValidationFlag>([
  'vat_rate_unexpected',
  'rate_line_mismatch',
  'arithmetic_mismatch',
  'de_number_format_ambiguous',
  'currency_non_eur',
  'document_type_review',
  'out_of_mvp_scope',
]);

/**
 * Synthesizes an EN-16931 BT-118 category code purely from the (server-validated)
 * VAT rate of a `normalizedBreakdown` line — fail-closed (T-2 §3).
 *
 *   19 | 7 → 'S'  (Normalbesteuerung §12 UStG)
 *   0      → 'E'  (mehrdeutig: §4-Befreiung vs. Pfand/nicht-steuerbar → sichere
 *                  Richtung `zero_exempt` → kein Vorsteuerabzug)
 *   null   → ''   (unerwarteter Satz; `deriveTaxScheme` ignoriert leere Codes,
 *                  das Overlay erzwingt ohnehin `needs_review`)
 *
 * @param rate - Geprüfter USt-Satz aus `normalizedBreakdown` (0 | 7 | 19 | null).
 * @returns BT-118-Kategorie-Code für die `synthInput.taxBreakdown`-Synthese.
 */
function synthCategoryCode(rate: number | null): string {
  if (rate === 19 || rate === 7) {
    return 'S';
  }
  if (rate === 0) {
    return 'E';
  }
  return '';
}

// ─── mapScannedReceiptToExpenseDraft ──────────────────────────────────────────

/**
 * Maps a `status==='extracted'` scanned receipt to an `ExpenseDraft`.
 *
 * The draft is reine Modal-Vorbefüllung: `status='draft'`,
 * `taxSchemeConfirmedByUser=false`, and — abweichend vom ZUGFeRD-Pfad —
 * `vatDeductible=false` IMMER (E-2). Eine `Expense` entsteht ausschließlich über
 * `createExpense` aus dem `ReceiptScanModal` nach Mensch-Bestätigung (§0.2 T-2,
 * kein Silent Booking).
 *
 * @param receipt - Das Staging-Dokument mit UNTRUSTED `extraction` +
 *                  server-validierten `normalizedBreakdown` / `total*Cents` /
 *                  `validationFlags`.
 * @param sellerContext - `{ isSmallBusiness? }`. Im Adapter NICHT konsumiert
 *                  (Signatur-Parität zu `mapReceivedInvoiceToExpenseDraft`); die
 *                  §15-Abzugsfähigkeit berechnet erst das Modal.
 * @returns Ein vollständig befüllter `ExpenseDraft` für das `ReceiptScanModal`.
 */
export function mapScannedReceiptToExpenseDraft(
  receipt: ScannedReceipt,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sellerContext: ExpenseSellerContext,
): ExpenseDraft {
  const extraction = receipt.extraction;
  const flags = receipt.validationFlags;

  // ── Vorberechnung (T-2 §1 / §5) ─────────────────────────────────────────────
  const isCredit = extraction.documentType === 'credit_note';
  const dateUnparseable = flags.includes('date_unparseable');
  const addrBlob = nonEmpty(extraction.supplierAddress)
    ? (extraction.supplierAddress as string).trim()
    : '';

  /** §17-Vorzeichen: credit_note erzwingt negative Cents (Quell-Vorzeichen mehrdeutig). */
  const sgn = (x: number | null): number => (isCredit ? -Math.abs(x ?? 0) : (x ?? 0));

  const signedTotalNet = sgn(receipt.totalNetCents);
  const signedTotalVat = sgn(receipt.totalVatCents);
  const signedTotalGross = sgn(receipt.totalGrossCents);

  // ── synthInput.taxBreakdown (pro normalizedBreakdown-Zeile, T-2 §1) ─────────
  // Nur Präsenz + categoryCode + exemptionReason werden von den reinen Funktionen
  // gelesen; die Beträge dienen ausschließlich der Struktur-Parität (unsigniert).
  const synthTaxBreakdown: ReceivedInvoiceTaxBreakdown[] = receipt.normalizedBreakdown.map(
    (line): ReceivedInvoiceTaxBreakdown => ({
      categoryCode: synthCategoryCode(line.vatRate),
      taxRate: line.vatRate ?? 0,
      taxBasisAmount: centsToEurosSafe(line.netCents),
      taxAmount: centsToEurosSafe(line.vatCents),
      exemptionReason: extraction.exemptionHint ?? '',
    }),
  );

  // ── synthInput.lineItems (§14 Abs.4 Nr.5 „Menge/Art" — nur Länge geprüft) ───
  const synthLineItems: ReceivedInvoiceLineItem[] = extraction.lineItems.map(
    (li, idx): ReceivedInvoiceLineItem => ({
      id: String(idx),
      productName: li.description,
      productDesc: '',
      quantity: li.quantity ?? 0,
      unitCode: '',
      netPrice: li.netAmount ?? 0,
      lineTotal: li.netAmount ?? 0,
      taxRate: li.vatRate ?? 0,
    }),
  );

  // ── synthInput — Wegwerf-Struktur (T-2 §1) ──────────────────────────────────
  // Hinweis: `sellerStreet/Zip/City = addrBlob` ist der W-3-Präsenz-Proxy
  // (`hasAddress` prüft nur Präsenz, nie Struktur). `buyer*` sind absichtlich leer
  // ('' statt null, da `Pick<ParsedZugferdData>` diese Felder als non-nullable
  // `string` typisiert — verhaltensidentisch, da nur Präsenz geprüft wird):
  // ein Beleg trägt keinen Empfänger → >250 € erzwingt `empfaenger_name_anschrift`.
  const synthInput: TaxCompletenessInput = {
    documentTypeCode: isCredit ? '381' : '380',
    documentNo: extraction.invoiceNumber ?? '',
    documentDate: dateUnparseable ? '' : (extraction.documentDate ?? ''),
    grandTotal: centsToEuros(signedTotalGross),
    netTotal: centsToEuros(signedTotalNet),
    sellerName: extraction.supplierName ?? '',
    sellerStreet: addrBlob,
    sellerZip: addrBlob,
    sellerCity: addrBlob,
    sellerTaxId: extraction.supplierTaxId ?? '',
    sellerVatId: extraction.supplierVatId ?? '',
    buyerName: '',
    buyerStreet: '',
    buyerZip: '',
    buyerCity: '',
    documentNotes: nonEmpty(extraction.exemptionHint)
      ? [extraction.exemptionHint as string]
      : [],
    taxBreakdown: synthTaxBreakdown,
    lineItems: synthLineItems,
    deliveryDate: extraction.serviceDate,
    billingPeriodStart: null,
    referencedInvoices: [],
  };

  // ── Fail-closed Scheme-Overlay (T-2 §4) ─────────────────────────────────────
  const hasTreatmentUncertainty = flags.some((f) => TREATMENT_UNCERTAINTY.has(f));
  const forcesReview =
    hasTreatmentUncertainty ||
    extraction.reverseChargeHint === true ||
    nonEmpty(extraction.exemptionHint) ||
    receipt.totalNetCents === null ||
    receipt.totalGrossCents === null;

  const finalScheme: TaxScheme = forcesReview
    ? 'needs_review'
    : deriveTaxScheme(synthInput);

  // ── §14/§15/§33-Vollständigkeit (autoritativ, W-2/W-3 über die reine Fn) ─────
  const { completenessStatus, missingMandatoryFields } = checkInputInvoiceCompleteness(
    synthInput,
    finalScheme,
  );

  // ── Betrags-Ableitung (signiert, T-2 §5) ────────────────────────────────────
  const netCents = signedTotalNet;
  const grossCents = signedTotalGross;
  const vatOwedCents = finalScheme === 'standard' ? signedTotalVat : 0;
  const roundingDiffCents =
    finalScheme === 'standard' ? grossCents - netCents - vatOwedCents : 0;
  const vatRate =
    receipt.normalizedBreakdown.length === 1
      ? (receipt.normalizedBreakdown[0].vatRate ?? null)
      : null;

  // ── vatBreakdown[] — pro normalizedBreakdown-Zeile (T-2 §6.1, E-1/E-2) ───────
  const vatBreakdown: ExpenseVatBreakdownEntry[] = receipt.normalizedBreakdown.map(
    (line): ExpenseVatBreakdownEntry => ({
      categoryCode: synthCategoryCode(line.vatRate),
      taxRate: line.vatRate ?? 0,
      netCents: sgn(line.netCents),
      vatOwedCents: finalScheme === 'standard' ? sgn(line.vatCents) : 0,
      // E-2: Draft nie abzugsfähig — die Abzugsfähigkeit entscheidet das Modal.
      vatDeductibleCents: 0,
    }),
  );

  // ── nonEurCurrency (EUR-only, M4-Erbe) ──────────────────────────────────────
  const nonEurCurrency =
    nonEmpty(extraction.currency) &&
    (extraction.currency as string).toUpperCase() !== 'EUR'
      ? extraction.currency
      : null;

  return {
    supplierName: extraction.supplierName ?? '',
    supplierVatId: nonEmpty(extraction.supplierVatId) ? extraction.supplierVatId : null,
    supplierTaxId: nonEmpty(extraction.supplierTaxId) ? extraction.supplierTaxId : null,
    invoiceNumber: nonEmpty(extraction.invoiceNumber) ? extraction.invoiceNumber : null,
    documentTypeCode: isCredit ? '381' : '380',

    netCents,
    vatOwedCents,
    // E-2-Kern-Regel (§0.1 T-2): Draft nie abzugsfähig.
    vatDeductibleCents: 0,
    grossCents,
    vatRate,
    vatBreakdown,
    roundingDiffCents,

    taxScheme: finalScheme,
    taxSchemeConfirmedByUser: false,
    // E-2-Kern-Regel: KEIN Auto-true aus OCR.
    vatDeductible: false,

    completenessStatus,
    missingMandatoryFields,

    expenseDate: dateUnparseable ? '' : (extraction.documentDate ?? ''),
    dueDate: null,
    serviceDate: nonEmpty(extraction.serviceDate) ? extraction.serviceDate : null,

    category: null,
    skrAccount: null,

    sourceReceivedInvoiceId: null,

    // ── Scan-Quelle (M13a) ────────────────────────────────────────────────────
    source: 'scan',
    sourceScannedReceiptId: receipt.id,
    receiptStorageRef: receipt.storageRef,

    nonEurCurrency,

    status: 'draft',
  };
}
